<template>
  <OktaLayout>
    <template #body>
      <!-- Stage 1 Basic Info -->
      <FormKit
        v-if="currentState == PageStates.MEMBER_DETAILS"
        v-model="partyMatchDetails"
        type="form"
        @submit="partyMatch"
      >
        <div class="gap-5 grid">
          <PanelTitle v-bind="formInputs.panelTitle" />
          <InformationBox
            v-if="identityNotFound"
            :type="InformationBoxTypes.ERROR"
            :description="formInputs.usernameUnrecoverable?.content?.value"
          />
          <RACQFormKit :bindings="formInputData('firstName')" />
          <RACQFormKit :bindings="formInputData('lastName')" />
          <DateOfBirth
            :bindings="formInputData('dob')"
            :invalidDobWarning="formInputs.dob?.invalidDobWarning?.value"
            :maxDateByDays="formInputs.dob?.maxDateByDays?.value"
            :minDate="formInputs.dob?.minDate?.value"
            :dateMustBefore="formInputs.dob?.dateMustBefore?.value"
            :dateMustAfter="formInputs.dob?.dateMustAfter?.value"
          />
          <RACQFormKit :bindings="formInputData('postcode')" />
          <FormKit
            type="submit"
            inputClass="btn btn-primary w-full"
            :label="formInputs.nextBtn?.label?.value"
            :loading="isLoading"
            :disabled="isLoading"
          />
        </div>
      </FormKit>
      <!-- Stage 2 Multiple Parties -->
      <FormKit
        v-else-if="currentState == PageStates.MEMBERSHIP_CARD_NUMBER"
        v-model="membershipCardDetails"
        type="form"
        @submit="verifyMembershipCardNumber"
      >
        <div class="pt-8 gap-5 grid">
          <InformationBox
            v-if="membershipCardMismatch"
            :type="InformationBoxTypes.ERROR"
            :description="formInputs.invalidMembershipNumber?.content?.value"
          />
          <Divider v-if="membershipCardMismatch" />
          <PanelTitle v-bind="formInputs.verifyMembershipTitle" />
          <RACQFormKit :bindings="formInputData('membershipCardInput')" />
          <RichText
            v-tooltip.bottom="{
              content: formInputs.whereCanIFIndMyNumber?.helpSm?.value,
              triggers: [],
              shown: isTooltipOpen,
              html: true,
            }"
            tag="button"
            :field="formInputs.whereCanIFIndMyNumber?.label"
            class="link w-fit"
            @click="toggleTooltip"
          />
          <FormKit
            type="submit"
            inputClass="btn btn-primary w-full"
            :label="formInputs.verifyBtn?.label?.value"
            :loading="isLoading"
            :disabled="isLoading"
          />
        </div>
      </FormKit>
      <!-- Stage 3 Result -->
      <div v-else-if="currentState == PageStates.LOCKED">
        <InformationBox
          :type="InformationBoxTypes.ERROR"
          :description="formInputs.accountHasBeenLocked?.content?.value"
        />
      </div>
      <div v-else-if="currentState == PageStates.FAILED">
        <RichText :field="formInputs.accountNotFound?.label" />
      </div>
      <div v-else-if="currentState == PageStates.SUCCESS" class="text-center">
        <RichText :field="formInputs.loginRecovered?.label" />
        <Tag :tagText="username" :tagType="TagTypes.person" :tagSize="TagSize.lg" outerClass="!rounded-full mb-9" />
        <FormKit
          type="button"
          inputClass="btn btn-primary w-full"
          :label="formInputs.loginBtn?.label?.value"
          :loading="isLoading"
          :disabled="isLoading"
          @click="goToLoginWithHint"
        />
      </div>
      <RichText v-if="currentState != PageStates.SUCCESS" :field="formInputs.backToLogin?.label" />
    </template>
    <template #disclaimer>
      <RichText :field="formInputs.disclaimer?.label" />
    </template>
  </OktaLayout>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useMsal } from '@/composables/useMsal';
import { Link, RichText } from '@sitecore-jss/sitecore-jss-vue';
import DateOfBirth from '@/components/DateInput/DateOfBirth.vue';
import Divider from '@/components/Divider/Divider.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import OktaLayout from '@/components/OktaLayout/OktaLayout.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import Tag from '@/components/Tag/Tag.vue';
import useFormInputData from '@/composables/useFormInputData';
import { ISO_DATE, STANDARD_DATE_FORMAT } from '@/constants/formats';
import { FormEvents } from '@/enums/form-events.enum';
import { TagSize, TagTypes } from '@/enums/tag-enums';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { useAccountStore } from '@/store';
import { useAnalyticsStore } from '@/store';
import { useUsernameRecoveryStore } from '@/store/username-recovery/username-recovery.state';
import { convertArrayToObject, dateStringToDate, dateToDateString } from '@/utils/data-transforms';
import { MatchStatus } from '@/interfaces/self-service/search-identity.interface';

const FORM_NAME = 'Recover login details';

enum PageStates {
  MEMBER_DETAILS = 'memberDetails',
  MEMBERSHIP_CARD_NUMBER = 'membershipCardNumber',
  SUCCESS = 'success',
  FAILED = 'failed',
  LOCKED = 'locked',
}

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();
const formInputs = convertArrayToObject(props.content);
const accountStore = useAccountStore();

const currentState = ref<PageStates>(PageStates.MEMBER_DETAILS);
const partyMatchDetails = ref<Record<string, string>>({});
const membershipCardDetails = ref<Record<string, string>>({});
const identityNotFound = ref(false);
const membershipCardMismatch = ref(false);
const isLoading = ref(false);
const isTooltipOpen = ref(false);
const username = ref('');
const { accounts } = useMsal();

const { formInputData } = useFormInputData(formInputs);
const store = useUsernameRecoveryStore();
const analyticsStore = useAnalyticsStore();

onMounted(() => {
  analyticsStore.pushAnalytics({
    event: FormEvents.START,
    form: {
      authStatus: accounts.value.length === 0 ? 'Not logged in' : 'Logged in',
      name: FORM_NAME,
      stepName: 'FormStarted',
    },
  });
});

const partyMatch = async () => {
  try {
    isLoading.value = true;
    identityNotFound.value = false;
    const dob = dateToDateString(dateStringToDate(partyMatchDetails.value.dob, STANDARD_DATE_FORMAT), ISO_DATE);
    const searchResult = await store.searchIdentities({
      firstName: partyMatchDetails.value.firstName,
      lastName: partyMatchDetails.value.lastName,
      postcode: partyMatchDetails.value.postcode,
      dateOfBirth: dob,
    });

    // Exact match
    if (searchResult.email) {
      completeRecovery(searchResult.email);
    }
    // Other status
    else {
      switch (searchResult.matchStatus) {
        case MatchStatus.NotRegistered:
          currentState.value = PageStates.FAILED;
          break;
        case MatchStatus.NoMatch:
          identityNotFound.value = true;
          break;
        case MatchStatus.Inactive:
          currentState.value = PageStates.LOCKED;
          analyticsStore.pushAnalytics({
            event: FormEvents.JEOPARDY,
            form: {
              authStatus: 'Not logged in',
              name: FORM_NAME,
              formErrorDetail: 'Your account has been locked out due to...',
              formErrorType: 'Locked out',
            },
          });
          break;
        case MatchStatus.MultiMatches:
          currentState.value = PageStates.MEMBERSHIP_CARD_NUMBER;
          analyticsStore.pushAnalytics({
            event: FormEvents.STEP,
            form: {
              authStatus: 'Not logged in',
              name: FORM_NAME,
              stepName: 'Verify with your membership card number',
            },
          });
          break;

        default:
          break;
      }
    }
  } catch {
    identityNotFound.value = true;
  }
  isLoading.value = false;
};

const verifyMembershipCardNumber = async () => {
  isLoading.value = true;
  const dob = dateToDateString(dateStringToDate(partyMatchDetails.value.dob, STANDARD_DATE_FORMAT), ISO_DATE);
  const searchResult = await store.searchIdentities({
    firstName: partyMatchDetails.value.firstName,
    lastName: partyMatchDetails.value.lastName,
    postcode: partyMatchDetails.value.postcode,
    dateOfBirth: dob,
    membershipNumber: membershipCardDetails.value.membershipCardInput?.replaceAll(' ', ''),
  });

  // Membership number mismatch
  if (searchResult.matchStatus == MatchStatus.MemberNumberMismatched) {
    membershipCardMismatch.value = true;
  } else if (searchResult.email) {
    completeRecovery(searchResult.email);
  }
  // Okta account not found
  else {
    currentState.value = PageStates.FAILED;
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        authStatus: accounts.value.length === 0 ? 'Not logged in' : 'Logged in',
        name: FORM_NAME,
        field: 'Login details not found',
        stepName: 'FormCompleted',
      },
    });
  }
  isLoading.value = false;
};

const completeRecovery = (email: string) => {
  username.value = email;
  currentState.value = PageStates.SUCCESS;
  analyticsStore.pushAnalytics({
    event: FormEvents.COMPLETE,
    form: {
      authStatus: 'Not logged in',
      name: FORM_NAME,
      field: 'Login details recovered',
      stepName: 'FormCompleted',
    },
  });
};

const goToLoginWithHint = async () => {
  isLoading.value = true;
  const url = await accountStore.getSelfServiceAccountOverviewUrl();
  await accountStore.signInWithHint(url, username.value);
};

const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
  if (isTooltipOpen.value) {
    setTimeout(() => {
      document.querySelector('.v-popper__inner')?.addEventListener('click', () => {
        isTooltipOpen.value = false;
      });
    }, 500);
  }
};
</script>
